import { v4 as uuid } from 'uuid';

import {
  formatColumnDateLabel,
  getPieChartDisplay,
  getTopLevelPieRows,
  MAX_PIE_CHART_ITEMS,
  shouldRenderPieChartSlice,
} from 'app-components/flux/generated-reports/generatedReportsSelectors';
import { transformResponseToReport } from 'app-components/flux/generated-reports/generatedReportsTransformers';
import type { Report } from 'app-components/flux/generated-reports/generatedReportsTypes';
import {
  availableTabNames,
  cloneDefaultReportConfiguration,
  cloneReportConfiguration,
} from 'app-components/flux/report-configurations/defaultReportConfigurations';
import { getReportConfigByName } from 'app-components/flux/report-configurations/defaults/reportConfigurationDefaultsSelectors';
import { transformReportConfigurationToRequestData } from 'app-components/flux/report-configurations/reportConfigurationsTransformers';
import type { ReportNameType } from 'app-components/flux/report-configurations/reportConfigurationsTypes';
import { getLogger, httpClientInterface, requireEnvironmentConfig } from 'app-components/utils/core';
import {
  DATE_RANGE_LABEL_BY_ID,
  type GeneratedReportRanges,
  type PREDEFINED_RANGE_VALUE,
  type Timestep,
} from 'app-components/utils/date';
import store from 'app-components/utils/redux-store';
import type RootState from 'app-components/utils/redux-store/rootState';

import { AI_CHAT_REDUCER_KEY, type ChartType } from '../../aiChatTypes';
import { safeParse } from '../../aiChatUtils';
import { cacheQueryResult, clearConversationCache } from '../../aiQueryCacheSlice';
import { returnToolResponse } from '../llmToolsFramework';
import type { ToolMessage } from '../llmToolsTypes';
import { isRecord, toFiniteNumberOrNull } from '../utils';

const log = getLogger('llmReportTools');

export function clearQueryCache(conversationId: string): void {
  store?.dispatch(clearConversationCache(conversationId));
}

const SUMMARY_REPORT_TYPES = new Set<string>([
  'net-income',
  'profit-loss',
  'income-expense',
  'savings',
  'net-worth',
  'balance-sheet',
]);

// Tells the LLM which chart types are meaningful for a given report.
// Summary reports (multi-period time-series) suit line/area/bar/column.
// Transaction/categorical reports (single-period breakdown) suit pie/bar/column/sidewaysBar.
function getSupportedVisualizations(reportType: string): ChartType[] {
  if (SUMMARY_REPORT_TYPES.has(reportType)) {
    return ['line', 'area', 'bar', 'column'];
  }
  return ['pie', 'bar', 'column', 'sidewaysBar'];
}

function summarizeReport(report: Report, timestep?: Timestep) {
  if (report.reportType === 'TRANSACTION') {
    const rowsByParentId = report.rows.reduce<Record<string, typeof report.rows>>((acc, row) => {
      const key = row.parentId ?? '-1';
      (acc[key] ??= []).push(row);
      return acc;
    }, {});
    const categories = getTopLevelPieRows(rowsByParentId);

    // Same sign convention as the pie chart selector: INCOME rows are already positive,
    // SPENDING rows are stored as negative so we flip them before passing to getPieChartDisplay.
    const negator = report.reportSubType === 'INCOME' ? 1 : -1;

    const items = categories
      .flatMap((r) => {
        const valueCell = r.cellData?.find((cell) => cell.value != null && !isNaN(Number(cell.value)));
        if (!valueCell) return [];
        const signedTotal = Number(valueCell.value) * negator;
        const { total } = getPieChartDisplay(report.reportSubType, signedTotal);
        if (!shouldRenderPieChartSlice(total)) return [];
        const isAnomalous = signedTotal < 0;
        // INCOME report: positive = income, negative = expense anomaly (and vice-versa for SPENDING)
        const isIncome = (report.reportSubType === 'INCOME') !== isAnomalous;
        const type = isIncome ? 'income' : 'expense';
        return [{ name: r.name || r.entityId, amount: Math.abs(signedTotal), type }];
      })
      .sort((a, b) => b.amount - a.amount);

    // Mirror the pie chart legend: cap at MAX_PIE_CHART_ITEMS and fold the rest into
    // an "Everything else" entry so the LLM describes exactly what the chart shows.
    const visibleItems =
      items.length > MAX_PIE_CHART_ITEMS
        ? [
            ...items.slice(0, MAX_PIE_CHART_ITEMS),
            {
              name: 'Everything else',
              amount: items.slice(MAX_PIE_CHART_ITEMS).reduce((sum, i) => sum + i.amount, 0),
              type: 'expense' as const,
            },
          ]
        : items;

    return { total: (report as any).total ?? null, categories: visibleItems };
  }

  // SUMMARY reports (net-income, net-worth, savings, etc.): top-level rows × time-period columns.
  // A trailing isAggSum column is a whole-range Total, not a period — drop it like
  // getGraphDataFromReport does, or it renders as an extra bar/point after the real periods.
  const topLevel = report.rows.filter((r) => (r.parentId === '-1' || !r.parentId) && r.entityId !== 'TOTAL');
  const columns = report.columns[report.columns.length - 1]?.isAggSum ? report.columns.slice(0, -1) : report.columns;
  return topLevel.map((r) => ({
    name: r.name || r.entityId,
    periods: columns.map((col, i) => ({
      period:
        col.headerValue && 'startOn' in col.headerValue
          ? formatColumnDateLabel(report.columns, timestep ?? 'MONTH', col.headerValue.startOn, col.headerValue.endOn)
          : col.headerValue && 'id' in col.headerValue
            ? col.headerValue.id
            : `col${i}`,
      amount: r.cellData?.[i]?.value ?? null,
    })),
  }));
}

const REPORT_TITLES: Record<string, string> = {
  'total-spending': 'Spending by Category',
  income: 'Income by Category',
  taxes: 'Taxes by Category',
  'net-income': 'Net Income Over Time',
  'profit-loss': 'Profit & Loss Over Time',
  'income-expense': 'Income vs Expenses',
  savings: 'Savings Over Time',
  'net-worth': 'Net Worth Over Time',
  'balance-sheet': 'Balance Sheet Over Time',
};

const CONFIG_CHART_TYPE_MAP: Record<string, ChartType | null> = {
  PIE: 'pie',
  NET_INCOME: 'line',
  MULTILINE: 'line',
  NONE: null,
};

// Curated date ranges exposed to the LLM — an intentional subset of GeneratedReportRanges
const LLM_DATE_RANGES = [
  'MONTH_TO_DATE',
  'YEAR_TO_DATE',
  'LAST_MONTH',
  'RECENT_6_MONTHS',
  'RECENT_12_MONTHS',
  'QUARTER_TO_DATE',
  'LAST_YEAR',
  'CUSTOM',
] as const satisfies ReadonlyArray<GeneratedReportRanges>;

// Short description per predefined report — TypeScript enforces one entry per availableTabNames entry
const REPORT_LLM_DESCRIPTIONS: Record<(typeof availableTabNames)[number], string> = {
  'total-spending': 'spending by category',
  income: 'income by category',
  'net-income': 'income vs expenses over time',
  'profit-loss': 'profit & loss over time',
  'income-expense': 'income and expenses compared over time',
  savings: 'savings by account over time',
  'net-worth': 'net worth over time',
  taxes: 'tax-related transactions',
  'balance-sheet': 'assets and liabilities',
};

// Generates the report-type menu embedded in the generate_report tool description.
// Reads defaults from cloneDefaultReportConfiguration so new report types are picked up automatically.
function buildReportTypeList(): string {
  return availableTabNames
    .map((name) => {
      const config = cloneDefaultReportConfiguration(name);
      const dateRangeLabel = config
        ? (DATE_RANGE_LABEL_BY_ID[config.dateRange as PREDEFINED_RANGE_VALUE] ?? config.dateRange)
        : 'unknown';
      const configChart = config?.chartType ?? '';
      const resolvedChart = configChart in CONFIG_CHART_TYPE_MAP ? CONFIG_CHART_TYPE_MAP[configChart] : null;
      const chartLabel = resolvedChart ?? 'none';
      return `- ${name.padEnd(16)} — ${REPORT_LLM_DESCRIPTIONS[name]} (default date range: ${dateRangeLabel}; default chart: ${chartLabel})`;
    })
    .join('\n');
}

const REPORT_TYPE_LIST = buildReportTypeList();

// Builds a compact summary from already-summarized report data for the cache entry.
function buildReportSummary(
  reportType: string,
  data: unknown,
  rows: unknown,
): { text: string; keyFacts: Record<string, string | number | boolean | null> } {
  if (isRecord(data) && Array.isArray((data as any).categories)) {
    const categories = (data as any).categories as Array<{ name: string; amount: number }>;
    const total = categories.reduce((sum, c) => sum + (c.amount ?? 0), 0);
    const rankedCategories = categories.filter((category) => category.name !== 'Everything else').slice(0, 3);
    const topCategoriesText = rankedCategories
      .map((category, index) => `${index + 1}. ${category.name} ($${category.amount.toFixed(2)})`)
      .join('; ');
    return {
      text: `${REPORT_TITLES[reportType] ?? reportType}: ${categories.length} categories, total $${total.toFixed(2)}${
        topCategoriesText ? `. Top categories: ${topCategoriesText}` : ''
      }`,
      keyFacts: {
        categoryCount: categories.length,
        totalSpend: parseFloat(total.toFixed(2)),
        topCategory1: rankedCategories[0]?.name ?? null,
        topAmount1: rankedCategories[0]?.amount ?? null,
        topCategory2: rankedCategories[1]?.name ?? null,
        topAmount2: rankedCategories[1]?.amount ?? null,
        topCategory3: rankedCategories[2]?.name ?? null,
        topAmount3: rankedCategories[2]?.amount ?? null,
      },
    };
  }

  if (Array.isArray(rows) && rows.length > 0) {
    type SummaryRow = { name: string; periods: Array<{ period: string; amount: unknown }> };
    const allRows = rows as SummaryRow[];
    // Prefer the "Net Worth" row for net-worth reports; fall back to first row
    const primaryRow = allRows.find((r) => /net.?worth/i.test(r.name)) ?? allRows[0];
    const periods = primaryRow?.periods ?? [];
    const latest = periods[periods.length - 1];
    const prev = periods[periods.length - 2];
    const latestAmount = toFiniteNumberOrNull(latest?.amount);
    const prevAmount = toFiniteNumberOrNull(prev?.amount);
    return {
      text: `${REPORT_TITLES[reportType] ?? reportType}: ${periods.length} period${periods.length !== 1 ? 's' : ''}`,
      keyFacts: {
        latestPeriod: latest?.period ?? null,
        latestValue: latestAmount,
        previousValue: prevAmount,
        periodCount: periods.length,
      },
    };
  }

  return { text: REPORT_TITLES[reportType] ?? reportType, keyFacts: {} };
}

export const reportTools = {
  generate_report: {
    definition: {
      name: 'generate_report',
      description: `Retrieve financial report data to answer user questions about their finances. Use this tool for income, spending, savings, taxes, and other report summaries. For current net worth and which accounts drive it, call get_net_worth instead. Use reportType net-worth only when the user asks for the net worth report over time. Do not ask them to run a report themselves.

NEVER produce a chart, graph, table, or ASCII visualisation yourself — always call a tool and let the UI render it.

When this tool returns, call render_chart with the resultId and a chartType from availableRenderHints whenever a chart would help — for example when the data is comparative, time-series, or the user asked for a visual.

Choose the report type that best matches the user's question:
${REPORT_TYPE_LIST}

Optionally override the date range with dateRange, startOn, endOn. Match the date range to what the user asked about — if they say "last month" use LAST_MONTH, "this year" use YEAR_TO_DATE, etc.`,
      parameters: {
        type: 'object' as const,
        properties: {
          reportType: {
            type: 'string' as const,
            enum: [...availableTabNames],
            description: 'The predefined report to generate',
          },
          dateRange: {
            type: 'string' as const,
            enum: [...LLM_DATE_RANGES],
            description: "Optional date range override. Defaults to the report type's predefined range.",
          },
          startOn: {
            type: 'string' as const,
            description: 'ISO date (YYYY-MM-DD). Required when dateRange is CUSTOM.',
          },
          endOn: {
            type: 'string' as const,
            description: 'ISO date (YYYY-MM-DD). Required when dateRange is CUSTOM.',
          },
        },
        required: ['reportType'],
      },
    },
    handler: async (msg: ToolMessage) => {
      const args = (safeParse(msg.arguments) || {}) as {
        reportType: ReportNameType;
        dateRange?: GeneratedReportRanges;
        startOn?: string;
        endOn?: string;
      };
      const { reportType, dateRange, startOn, endOn } = args;

      if (dateRange === 'CUSTOM' && (!startOn || !endOn)) {
        return returnToolResponse(msg, {
          success: false,
          error: 'startOn and endOn are required when dateRange is CUSTOM',
        });
      }

      const overrides: Record<string, unknown> = { source: 'AI_CHAT' };
      if (dateRange) overrides.dateRange = dateRange;
      if (startOn) overrides.startOn = startOn;
      if (endOn) overrides.endOn = endOn;

      const qcsDefault = store ? getReportConfigByName(store.getState(), { name: reportType }) : undefined;
      const config = qcsDefault
        ? cloneReportConfiguration(qcsDefault, overrides)
        : cloneDefaultReportConfiguration(reportType, false, false, overrides);
      if (!config) {
        return returnToolResponse(msg, {
          success: false,
          error: `Unknown report type "${reportType}". Valid: ${availableTabNames.join(', ')}`,
        });
      }

      const url = `${requireEnvironmentConfig().services_url}/reports/generate`;
      const body = transformReportConfigurationToRequestData(config);

      // Backend rejects predefined dateRange combined with explicit start/end bounds
      if (body.dateRange && body.dateRange !== 'CUSTOM') {
        delete body.startOn;
        delete body.endOn;
      }

      log.debug('generate_report: calling', { reportType, dateRange });

      try {
        const response = await httpClientInterface.send('POST', url, body);
        const report = transformResponseToReport(response as any, config);
        const summary = summarizeReport(report, config.timestep);
        const isSummary = report.reportType !== 'TRANSACTION';
        const data = isSummary ? undefined : summary;
        const rows = isSummary ? summary : undefined;

        const reportId = `rpt_${uuid()}`;

        const state = store?.getState() as RootState | undefined;
        const conversationId = state?.[AI_CHAT_REDUCER_KEY]?.currentConversationId ?? 'session';
        const dataShape = isSummary ? ('trend' as const) : ('breakdown' as const);

        // For TRANSACTION reports fullData must be the categories array so applyFilters
        // can iterate it directly. The { total, categories } envelope isn't chartable as-is.
        const cacheFullData = isSummary
          ? rows
          : isRecord(data) && Array.isArray((data as any).categories)
            ? (data as any).categories
            : data;

        store?.dispatch(
          cacheQueryResult({
            resultId: reportId,
            conversationId,
            queryKind: reportType,
            fullData: cacheFullData,
            summary: buildReportSummary(reportType, data, rows),
            dataShape,
            cachedAt: Date.now(),
            sourceContext: {
              reportType,
              startOn: report.startOn,
              endOn: report.endOn,
            },
          }),
        );

        return returnToolResponse(msg, {
          success: true as const,
          report_id: reportId,
          resultId: reportId,
          availableRenderHints: getSupportedVisualizations(reportType),
          reportType,
          subType: report.reportSubType,
          startOn: report.startOn,
          endOn: report.endOn,
          ...(isSummary ? { rows } : { data }),
        });
      } catch (error) {
        return returnToolResponse(msg, {
          success: false,
          error: String(error),
        });
      }
    },
  },
};
